<script setup lang="ts">
import { ref, onMounted } from "vue";
import type { CmsElementFaqTabs } from "~/composables/useCustom";

const props = defineProps<{
  content: CmsElementFaqTabs;
}>();

const { headlineText, mainText, button, Faqs } = props.content.data;

// Track which accordion is currently open
const activeAccordion = ref<number | null>(null);

// Toggle accordion function
const toggleAccordion = (index: number) => {
  activeAccordion.value = activeAccordion.value === index ? null : index;
};

const buttonTarget = computed(() => (button?.newTab ? "_blank" : "_self"));

// Update content heights after mount
onMounted(() => {
  // Get all accordion content elements
  const accordionContents = document.querySelectorAll(".accordion-content");

  // Set custom property for each accordion content
  accordionContents.forEach((content) => {
    const contentInner = content.querySelector(".content-inner");
    if (contentInner) {
      const height = contentInner.getBoundingClientRect().height;
      content.style.setProperty("--content-height", `${height}px`);
    }
  });
});

useFAQJsonLD(Faqs);
</script>

<template>
  <div class="faq px-4">
    <div class="container mx-auto relative py-10">
      <div class="flex flex-col lg:flex-row justify-between py-0 ">
        <div class="title mb-0 w-full lg:w-[305px]">
          <h3
            v-if="headlineText"
            class="h3-font leading-tight text-chinese-black mb-4 md:mb-4 lg:mb-6"
          >
            {{ headlineText }}
          </h3>
          <div
            v-if="mainText"
            class="text-base "
            v-html="mainText"
          ></div>
          <SharedButton
            v-if="button"
            :button="button"
            class="btn-default mt-4 md:mt-4 lg:mt-6  btn-w-icon btn-black-bg inline-block"
            :isInnerHtml="true"
          >
          </SharedButton>
        </div>

        <div class="w-full lg:w-[793px]">
          <div class="accordion w-full mx-auto">
            <div
              v-for="(item, index) in Faqs"
              :key="index"
              class="accordion-item bg-change bg-light-grey rounded-2xl overflow-hidden"
              :class="{ 'mb-4': index !== Faqs.length - 1 }"
            >
              <button
                v-if="item.faqTitle"
                class="accordion-header flex justify-between items-center w-full py-5 px-6 font-medium focus:outline-none rounded-tl-2xl rounded-tr-2xl rounded-bl-[0px] rounded-br-[0px]"
                @click="toggleAccordion(index)"
                :class="{ 'bg-dark-bg text-white': activeAccordion === index }"
              >
                <span class="font-bold leading-normal text-left">
                  {{ item.faqTitle }}
                </span>
                <svg
                  class="w-2 h-2 transform transition-transform accordion-icon"
                  :class="{ 'rotate-180': activeAccordion === index }"
                  viewBox="0 0 8 5"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3.51984 4.17398L0.368716 1.02288C0.325249 0.979409 0.291016 0.930875 0.266016 0.877275C0.241016 0.823658 0.228516 0.766217 0.228516 0.70495C0.228516 0.582417 0.269949 0.475959 0.352816 0.385576C0.435666 0.295193 0.544874 0.25 0.680441 0.25H7.31884C7.45441 0.25 7.56362 0.295626 7.64647 0.386876C7.72933 0.478143 7.77076 0.584609 7.77076 0.706276C7.77076 0.736709 7.72397 0.842309 7.63037 1.02308L4.47944 4.174C4.40701 4.24643 4.33213 4.29932 4.25482 4.33265C4.17748 4.36598 4.09242 4.38265 3.99964 4.38265C3.90686 4.38265 3.8218 4.36598 3.74447 4.33265C3.66715 4.29932 3.59227 4.24643 3.51984 4.17398Z"
                    :fill="activeAccordion === index ? '#fff' : '#141414'"
                  />
                </svg>
              </button>

              <div
                v-if="item.faqText"
                class="accordion-content overflow-hidden"
                :style="{
                  maxHeight:
                    activeAccordion === index ? 'var(--content-height)' : '0',
                }"
              >
                <div class="content-inner px-5 py-4">
                  <p
                    class="text-sm text-chinese-black font-normal leading-normal"
                  >
                    {{ item.faqText }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.accordion-icon {
  transition: transform 0.3s ease;
}

.accordion-content {
  transition: max-height 0.3s ease-in-out;
  --content-height: 200px; /* This is a default value, will be updated by JS */
}

.content-inner {
  transition: opacity 0.3s ease;
}
</style>
